/**
 * Entity factories: create units / buildings / resource nodes from resolved
 * defs and attach the right component mix. Adding a new unit behavior usually
 * means optionally setting a component here + writing a system.
 *
 * Hex world: units are mobile (continuous world-space pos, do not block a hex).
 * Buildings and resource nodes OCCUPY a single hex (col,row) — they block it
 * for pathing, and gatherers/builders path to an adjacent hex to interact.
 */
import type { World } from "./world";
import type { Registry } from "../data/registry";
import type { Vec2, Eid } from "./types";
import { propTileCost, buildingTileCost } from "./pathfinding";
import { propRadius, buildingRadius } from "./systems/movement";

export function spawnUnit(
  world: World,
  reg: Registry,
  civId: string,
  unitId: string,
  player: number,
  pos: Vec2
): Eid {
  const def = reg.unit(civId, unitId);
  const eid = world.eid();
  const color = world.players.get(player)?.color ?? "#fff";

  world.transform.set(eid, {
    pos: { ...pos },
    // Collision/contact radius — used ONLY for combat reach (range + myR + tR).
    // Kept close to the VISIBLE body (~0.1 tile) so "in melee range" means the
    // models are actually touching, not a couple of metres apart.
    radius: (def.radius ?? 0.18) * world.size,
    facing: 0,
  });
  world.owner.set(eid, { player });
  world.ident.set(eid, {
    defId: unitId,
    kind: "unit",
    civ: civId,
    cls: def.class,
    name: def.name,
    population: def.population,
  });
  world.health.set(eid, {
    hp: def.hp,
    maxHp: def.hp,
    armor: def.armor,
  });
  // def.speed is authored in TILES/sec; movement integrates in world units, so
  // convert by the world distance between adjacent hex centers (√3 · size).
  // This keeps a unit's tiles-per-second feel identical across map resolutions.
  const tile = Math.sqrt(3) * world.size;
  world.move.set(eid, { speed: def.speed * tile, path: [], queue: [] });
  if (def.attack) {
    // Combat reach in combat.ts is `range + myRadius + targetRadius` (edge-to-
    // edge). For RANGED units the authored value is real tiles → world units.
    // For MELEE (range ≤ 1 tile) that double-counts: a 1-tile weapon PLUS both
    // body radii let swords connect ~1.4 tiles out. A blade's reach is basically
    // contact, so melee gets a short fixed swing (added to the radii) — the
    // sword now lands only when the bodies are practically touching.
    const melee = def.attack.range <= 1;
    world.combat.set(eid, {
      damage: def.attack.damage,
      range: melee ? world.size * 0.15 : def.attack.range * tile,
      cooldown: def.attack.cooldown,
      cooldownLeft: 0,
      bonus: def.attack.bonus,
      bonusBuilding: def.attack.bonusBuilding,
    });
  }
  // naval transport / garrison carrier
  if (def.capacity && def.capacity > 0) {
    world.garrison.set(eid, { capacity: def.capacity, occupants: [] });
  }
  if (def.heal) {
    world.healer.set(eid, {
      range: def.heal.range * tile, // authored in TILES → world units
      rate: def.heal.rate,
      active: false,
    });
  }
  if (def.canGather) {
    world.gatherer.set(eid, {
      resource: null,
      carrying: 0,
      capacity: def.carryingCapacity ?? 10,
      node: null,
      dropSite: null,
      state: "toResource",
      timer: 0,
    });
  }
  if (def.canBuild) {
    world.builder.set(eid, { foundation: null, rate: 1 });
  }
  world.los.set(eid, { los: def.los * tile });

  // population accounting
  const ps = world.players.get(player);
  if (ps) ps.population += def.population;

  world.emit({ type: "spawn", tick: 0, at: { ...pos }, color });
  return eid;
}

/** Spawn a building occupying hex (origin.x=col, origin.y=row). */
export function spawnBuilding(
  world: World,
  reg: Registry,
  civId: string,
  buildingId: string,
  player: number,
  origin: Vec2,
  asFoundation: boolean
): Eid {
  const def = reg.building(civId, buildingId);
  const eid = world.eid();
  const color = world.players.get(player)?.color ?? "#fff";
  const center = world.hexCenter(origin.x, origin.y);

  world.transform.set(eid, {
    pos: { ...center },
    // Contact radius = the building's MODEL footprint (so you melee/interact when
    // your model touches its model — a big town centre is reachable from its real
    // edge, a small house only up close), floored at 0.35·size so even tiny
    // buildings keep enough reach margin for an attacker settling at the door.
    radius: Math.max(buildingRadius(buildingId, world.size), world.size * 0.35),
    facing: 0,
  });
  world.owner.set(eid, { player });
  world.ident.set(eid, {
    defId: buildingId,
    kind: "building",
    civ: civId,
    name: def.name,
  });
  const startHp = asFoundation ? Math.max(1, Math.round(def.hp * 0.1)) : def.hp;
  world.health.set(eid, {
    hp: startHp,
    maxHp: def.hp,
    armor: 0,
  });
  world.building.set(eid, {
    size: def.size,
    origin: { ...origin },
    isFoundation: asFoundation,
    constructionProgress: asFoundation ? 0 : 1,
    populationCap: def.population,
    isDropSite: !!def.isDropSite,
    isCore: !!def.isCore,
    beingBuilt: asFoundation,
    solid: !!def.solid,
    gate: !!def.gate,
  });
  world.los.set(eid, { los: def.los });
  // rally/spawn point: one hex east of the centre for land buildings. A dock
  // sits in the water, so a due-east default lands in open sea on the opposite
  // side from its gangway — point its rally at the connected shore hex instead.
  let rallyPt: Vec2 = { x: center.x + world.size, y: center.y };
  if (world.isWater(origin.x, origin.y)) {
    const shore = world.dockShore(origin.x, origin.y);
    if (shore) rallyPt = world.hexCenter(shore[0], shore[1]);
  }
  world.rally.set(eid, rallyPt);

  // trains? derived per civ from units
  const trains = reg.trainedAt(civId, buildingId);
  if (trains.length > 0 && !asFoundation) {
    world.producer.set(eid, { queue: [] });
  }

  // block the occupied hex for BUILD placement
  world.setBlocked(origin.x, origin.y, true);
  // Movement occupancy is separate from build-tile occupancy: docks block their
  // water tile, while land buildings/walls/gates use continuous model geometry.
  const k = world.hkey(origin.x, origin.y);
  const water = world.isWater(origin.x, origin.y);
  // A LAND building's tile is PASSABLE-occupied ground (buildingHexKeys) at ALL
  // times — including while it's a foundation — so a half-built wall never traps a
  // passer-by and its own builder can stand on it to raise it. A footprint
  // building also gets a sub-tile repel circle (route around the model); a wall /
  // gate / field gets none. Water (dock) registers nothing here — the sea is
  // naval-only ground until the pier goes solid.
  if (!water) {
    world.buildingHexKeys.add(k);
    const r = def.solid || def.gate ? 0 : buildingRadius(buildingId, world.size);
    if (r > 0) {
      world.propCost.set(k, buildingTileCost(buildingId));
      world.propPos.set(k, { x: center.x, z: center.y, r });
    }
  }
  // Completed structures are indexed here for dock occupancy, rampart geometry,
  // renderer connectivity, and gate ownership. Foundations remain non-colliding.
  if (!asFoundation) {
    if (water) world.solidHexKeys.add(k);
    else if (def.gate) world.gateHexKeys.set(k, player);
    else if (def.solid) world.solidHexKeys.add(k);
  }

  // a TOWER garrisons units (each adds an arrow) and fires a defensive volley —
  // the towerSystem skips foundations, so attaching here (even on a foundation) is safe.
  if (def.garrison) world.garrison.set(eid, { capacity: def.garrison, occupants: [] });
  if (def.attack) {
    const tile = Math.sqrt(3) * world.size;
    world.combat.set(eid, {
      damage: def.attack.damage,
      range: def.attack.range * tile, // authored in TILES → world units
      cooldown: def.attack.cooldown,
      cooldownLeft: 0,
      bonus: def.attack.bonus,
      bonusBuilding: def.attack.bonusBuilding,
    });
  }

  // economy hooks
  const ps = world.players.get(player);
  if (ps && !asFoundation && def.population) {
    ps.populationCap += def.population;
  }
  if (!asFoundation && def.isDropSite) {
    let set = world.dropsitesByPlayer.get(player);
    if (!set) {
      set = new Set();
      world.dropsitesByPlayer.set(player, set);
    }
    set.add(eid);
  }

  if (!asFoundation) {
    attachFieldResource(world, reg, eid);
    world.emit({ type: "spawn", tick: 0, at: { ...center }, color });
  }
  return eid;
}

/**
 * Turn a completed "worked building" (a field) into a harvestable resource:
 * attach a Resource component and mark its hex walk-on-able so farmers gather
 * directly from it. No-op for ordinary buildings. Called on construction
 * completion and for any field that spawns already finished.
 */
export function attachFieldResource(world: World, reg: Registry, eid: Eid): void {
  const idn = world.ident.get(eid);
  const b = world.building.get(eid);
  if (!idn?.civ || !b || world.resource.has(eid)) return;
  const def = reg.building(idn.civ, idn.defId);
  if (!def.gatherResource) return;
  const amount = def.gatherAmount ?? 1000;
  world.resource.set(eid, {
    resource: def.gatherResource,
    amount,
    perGather: def.gatherPerGather ?? 2,
    gatherTime: def.gatherTime ?? 1,
    cap: def.gatherCap,
    regen: def.gatherRegen,
    max: amount,
  });
  // a field occupies its hex for BUILDING but stays passable for movement, so
  // farmers can stand on the crop rows (same trick as resource-node tiles).
  world.resourceHexKeys.add(world.hkey(b.origin.x, b.origin.y));
}

/** Per-species wild-animal stats. Speed/radius in TILES (scaled by tile at
 *  spawn); food is the meat yield of the carcass. */
const FAUNA = {
  // speeds are kept a touch above a soldier's (0.55) so hunting is a chase, but
  // the brief flee-then-graze cadence lets infantry close in; cavalry catches easily.
  rabbit: { hp: 12, speed: 0.6, radius: 0.12, food: 35, aggressive: false, damage: 0 },
  deer: { hp: 30, speed: 0.58, radius: 0.2, food: 110, aggressive: false, damage: 0 },
  boar: { hp: 55, speed: 0.55, radius: 0.22, food: 150, aggressive: true, damage: 9 },
} as const;

export type Species = keyof typeof FAUNA;

/** Spawn a wandering wild animal (no owner). Killable → leaves a meat carcass. */
export function spawnAnimal(world: World, species: Species, pos: Vec2): Eid {
  const f = FAUNA[species];
  const eid = world.eid();
  const tile = Math.sqrt(3) * world.size;
  world.transform.set(eid, {
    pos: { ...pos },
    radius: f.radius * world.size,
    facing: world.rng.next() * Math.PI * 2,
  });
  world.ident.set(eid, {
    defId: species,
    kind: "animal",
    name: species.charAt(0).toUpperCase() + species.slice(1),
  });
  world.health.set(eid, { hp: f.hp, maxHp: f.hp, armor: 0 });
  world.move.set(eid, { speed: f.speed * tile, path: [], queue: [] });
  world.fauna.set(eid, {
    species,
    wanderCd: world.rng.next() * 4,
    fleeFor: 0,
    fleeFromX: 0,
    fleeFromZ: 0,
    aggressive: f.aggressive,
    food: f.food,
  });
  // boars fight back — give them a melee bite (short contact range like infantry)
  if (f.aggressive) {
    world.combat.set(eid, { damage: f.damage, range: world.size * 0.15, cooldown: 1.5, cooldownLeft: 0 });
  }
  return eid;
}

/** Spawn a resource node occupying hex (origin.x=col, origin.y=row). */
export function spawnResourceNode(
  world: World,
  reg: Registry,
  nodeDefId: string,
  origin: Vec2
): Eid {
  const def = reg.resourceNode(nodeDefId);
  const eid = world.eid();
  // Organic scatter: nudge the node OFF the hex centre (deterministic via rng).
  // This is the node's REAL position now — so the rendered prop, its collision
  // footprint and its gather point all line up (the renderer no longer offsets
  // it separately, which made trunks float away from their collision circle).
  const c = world.hexCenter(origin.x, origin.y);
  const ang = world.rng.next() * Math.PI * 2;
  const rad = world.rng.next() * 0.22 * world.size;
  const pos = { x: c.x + Math.cos(ang) * rad, y: c.y + Math.sin(ang) * rad };
  world.transform.set(eid, { pos, radius: 0.45, facing: 0 });
  world.ident.set(eid, {
    defId: nodeDefId,
    kind: "resource",
    name: def.name,
  });
  world.resource.set(eid, {
    resource: def.resource,
    amount: def.amount,
    perGather: def.perGather,
    gatherTime: def.gatherTime,
  });
  // Occupy the hex: blocks BUILDING here, but resource hexes stay passable for
  // movement (tracked separately) so units weave through forests. A per-size
  // routing cost makes A* prefer going AROUND the footprint when there's room.
  world.setBlocked(origin.x, origin.y, true);
  world.resourceHexKeys.add(world.hkey(origin.x, origin.y));
  world.propCost.set(world.hkey(origin.x, origin.y), propTileCost(nodeDefId));
  world.propPos.set(world.hkey(origin.x, origin.y), { x: pos.x, z: pos.y, r: propRadius(nodeDefId, world.size) });
  return eid;
}

/**
 * Spawn a FISH SCHOOL on a WATER hex (origin.x=col, origin.y=row). Unlike a land
 * resource node, a fish school does NOT register hex occupancy: water is already
 * blocked + impassable to land, and adding it to resourceHexKeys/propCost would
 * (a) make the sea tile land-walkable and (b) deflect ships as if it were a rock.
 * It's reached on the naval domain (passableFor "naval" = any water tile) and
 * harvested by a fishing boat exactly like any other resource node.
 */
export function spawnFish(
  world: World,
  reg: Registry,
  nodeDefId: string,
  origin: Vec2
): Eid {
  const def = reg.resourceNode(nodeDefId);
  const eid = world.eid();
  const c = world.hexCenter(origin.x, origin.y);
  // deterministic off-centre scatter (visual variety), kept well inside the hex
  const ang = world.rng.next() * Math.PI * 2;
  const rad = world.rng.next() * 0.18 * world.size;
  const pos = { x: c.x + Math.cos(ang) * rad, y: c.y + Math.sin(ang) * rad };
  world.transform.set(eid, { pos, radius: 0.4, facing: 0 });
  world.ident.set(eid, { defId: nodeDefId, kind: "resource", name: def.name });
  world.resource.set(eid, {
    resource: def.resource,
    amount: def.amount,
    perGather: def.perGather,
    gatherTime: def.gatherTime,
  });
  return eid;
}
